/**
 * 12 - Delayed Sabotage (03 §2): plant a time bomb at the aviation fuel dump - every bomb
 * is pre-set to "midnight" and goes off after the mission. Being spotted within 25 m of the
 * dump - of ANY tank of it, ROUND5 §2.5 - before or after, means the bomb is found; the
 * intended path is a uniform from the guard house. Alert 3 anywhere doubles the dump's sentries.
 */

import type { ObjectiveRule } from '../../sim/types';
import type { Point } from '../types';
import {
  checker, clutter, countKind, guard, guardAtDoor, guardsAbout, required, ruleOf, structure, targetsOf, uniformDonor,
} from './helpers';
import type { ArchetypeSpec } from './types';

type Watched = Extract<ObjectiveRule, { type: 'notSeenNear' }>;

/** "SEEN WITHIN 25 M OF THE DUMP" - of the dump, which is every tank of it. */
const SEEN_RADIUS = 25;
const SEEN_ORDERS = 'DO NOT BE SEEN WITHIN 25 M OF THE FUEL DUMP, BEFORE OR AFTER THE CHARGE IS SET';
const SEEN_REASON = 'SEEN AT THE FUEL DUMP - THE BOMB WAS FOUND';

export const delayedSabotage: ArchetypeSpec = {
  id: 'delayed-sabotage',
  name: 'DELAYED SABOTAGE',
  nativeBiome: 'arctic',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [48, 58], d: [30, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const dump = required(g, 'fuelTank', { target: true, from: placed, minDist: 0, margin: 2 });
    // The rest of the dump: more tanks close by, drums between them. A charge at any of them counts:
    // a player cannot be expected to know which tank the planners meant.
    const tanks = [dump];
    for (let i = 0; i < 1 + g.rng.int(2); i++) {
      const tank = structure(g, 'fuelTank', { target: true, near: dump, maxDist: 12, minDist: 7, from: placed, margin: 1.2 });
      if (tank) tanks.push(tank);
    }
    clutter(g, 'barrel', 4, dump, 7);
    const house = required(g, 'guardHouse', { minDist: 14, from: placed, margin: 1.4 });
    structure(g, 'hut', { minDist: 10, from: placed });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);

    guardAtDoor(g, house);
    guard(g, dump.x - 5, dump.z + 4, -0.6);
    guard(g, dump.x + 5, dump.z + 4, 0.6);
    guardsAbout(g, g.rng.int(2));
    // ROUND5 §2.5 (the marker audit): every tank was flagged the same and the 25 m rule watched only ONE of
    // them, so the map said "the dump is all of these" and the sim said "the dump is that one". The words are
    // right - a charge at any tank counts, and the dump is all of them - so the rule is anchored to each.
    // They share their words, and `ordersBlock` keeps one line per distinct text.
    const watched: ObjectiveRule[] = tanks.map((t) => ({
      type: 'notSeenNear', propId: t.id, radius: SEEN_RADIUS, orders: SEEN_ORDERS, reason: SEEN_REASON,
    }));
    return {
      objective: {
        kind: 'plantAtTarget', label: 'SABOTAGE FUEL DUMP', targetPropIds: tanks.map((t) => t.id), minFraction: 1,
        rules: [
          { type: 'bombsHoldUntilMidnight' },
          ...watched,
          { type: 'alertConsequence', level: 3, delaySeconds: 0, effect: 'reinforceTarget' },
          { type: 'recall', mode: 'normal' },
        ],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('delayed-sabotage');
    const dump = targetsOf(def, 'fuelTank')[0];
    const tanks = countKind(def, 'fuelTank');
    check(
      dump !== undefined && targetsOf(def, 'fuelTank').length === tanks && def.objective.targetPropIds.length === tanks,
      'every tank of the fuel dump must be a target: a charge at any of them counts',
    );
    check(def.objective.kind === 'plantAtTarget', 'objective must be plantAtTarget');
    check(ruleOf(def, 'bombsHoldUntilMidnight') !== undefined, 'bombs must hold until midnight');
    // ROUND5: the marked tanks and the watched tanks are the same set, so the map and the rule tell one story.
    const watched = def.objective.rules.filter((r): r is Watched => r.type === 'notSeenNear');
    check(watched.length === tanks, `${tanks} tanks are marked and ${watched.length} are watched: the dump is all of them`);
    check(
      watched.every((r) => r.radius === SEEN_RADIUS && r.orders === SEEN_ORDERS && r.reason === SEEN_REASON),
      `every tank must be watched at ${SEEN_RADIUS} m, in the same words`,
    );
    for (const t of targetsOf(def, 'fuelTank')) {
      check(watched.some((r) => r.propId === t.id), `fuel tank ${t.id} is marked on the map and watched by nothing`);
    }
    check(watched[0]?.propId === dump?.id, 'the dump proper must be the first tank watched');
    check(ruleOf(def, 'alertConsequence')?.effect === 'reinforceTarget', 'alert 3 must double the dump sentries');
    check(countKind(def, 'guardHouse') >= 1, 'needs a guard house: the uniform is the intended way in');
    check(uniformDonor(def, 'guardHouse'), 'a guard must stand at the guard house: his is the uniform to take');
    return out;
  },
};
